'use client';
import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import AppLogo from './ui/AppLogo';
import { LayoutDashboard, Users, CalendarDays, Wrench, PackageSearch, FileBarChart2, Settings, LogOut, ChevronLeft, ChevronRight, CreditCard, Bell, UserCircle, Briefcase } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';

interface NavItem {
  label: string;
  href: string;
  icon: React.ReactNode;
  badge?: number;
}

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
  portal: 'admin' | 'customer' | 'technician';
}

const adminNav: NavItem[] = [
  { label: 'Dashboard', href: '/', icon: <LayoutDashboard size={20} /> },
  { label: 'Customers', href: '/customer-management', icon: <Users size={20} /> },
  { label: 'Scheduling', href: '/scheduling-booking-management', icon: <CalendarDays size={20} /> },
  { label: 'Technicians', href: '/technician-field-interface', icon: <Wrench size={20} /> },
  { label: 'Inventory', href: '#', icon: <PackageSearch size={20} /> },
  { label: 'Payments', href: '#', icon: <CreditCard size={20} /> },
  { label: 'Reports', href: '#', icon: <FileBarChart2 size={20} /> },
  { label: 'Notifications', href: '#', icon: <Bell size={20} /> },
  { label: 'Settings', href: '#', icon: <Settings size={20} /> },
];

const customerNav: NavItem[] = [
  { label: 'My Dashboard', href: '/customer-dashboard', icon: <LayoutDashboard size={20} /> },
  { label: 'Service History', href: '#', icon: <Briefcase size={20} /> },
  { label: 'Payments', href: '#', icon: <CreditCard size={20} /> },
  { label: 'My Profile', href: '#', icon: <UserCircle size={20} /> },
  { label: 'Support', href: '#', icon: <Bell size={20} /> },
];

const technicianNav: NavItem[] = [
  { label: 'My Jobs', href: '/technician-field-interface', icon: <Briefcase size={20} /> },
  { label: 'Schedule', href: '/scheduling-booking-management', icon: <CalendarDays size={20} /> },
  { label: 'My Earnings', href: '#', icon: <CreditCard size={20} /> },
  { label: 'Profile', href: '#', icon: <UserCircle size={20} /> },
];

const navMap = {
  admin: adminNav,
  customer: customerNav,
  technician: technicianNav,
};

export default function Sidebar({ collapsed, onToggle, portal }: SidebarProps) {
  const pathname = usePathname();
  const router = useRouter();
  const navItems = navMap[portal];
  const supabase = createClient();

  const [userEmail, setUserEmail] = useState('');
  const [userInitials, setUserInitials] = useState('');
  const [userName, setUserName] = useState('');

  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (user) {
        setUserEmail(user.email || '');
        const name = user.user_metadata?.full_name || user.email?.split('@')[0] || '';
        setUserName(name);
        setUserInitials(
          name
            .split(' ')
            .map((n: string) => n[0])
            .join('')
            .toUpperCase()
            .slice(0, 2) || user.email?.[0]?.toUpperCase() || '?'
        );
      }
    });
  }, []);

  async function handleSignOut() {
    await supabase.auth.signOut();
    router.push('/sign-up-login-screen');
    router.refresh();
  }

  return (
    <aside
      className={`relative flex flex-col bg-card border-r border-border sidebar-transition z-30 shrink-0 ${
        collapsed ? 'w-16' : 'w-60'
      }`}
    >
      {/* Logo */}
      <div className="flex items-center gap-2 px-3 py-4 border-b border-border min-h-[64px]">
        <AppLogo size={32} />
        {!collapsed && (
          <div className="flex flex-col leading-tight overflow-hidden">
            <span className="font-bold text-sm text-primary truncate">Leenistech</span>
            <span className="text-xs text-muted-foreground truncate">Water Purification</span>
          </div>
        )}
      </div>

      {/* Portal label */}
      {!collapsed && (
        <div className="px-3 pt-3 pb-1">
          <span className="text-xs font-600 uppercase tracking-widest text-muted-foreground">
            {portal === 'admin' ? 'Admin Portal' : portal === 'customer' ? 'Customer Portal' : 'Technician Portal'}
          </span>
        </div>
      )}

      {/* Nav items */}
      <nav className="flex-1 overflow-y-auto py-2 px-2 space-y-0.5">
        {navItems.map((item) => {
          const isActive = pathname === item.href || (item.href !== '/' && pathname.startsWith(item.href));
          return (
            <Link
              key={`nav-${item.label}`}
              href={item.href}
              title={collapsed ? item.label : undefined}
              className={`flex items-center gap-3 px-2 py-2 rounded-lg text-sm font-medium transition-all duration-150 relative group ${
                isActive ? 'nav-active' : 'nav-item'
              }`}
            >
              <span className="shrink-0">{item.icon}</span>
              {!collapsed && <span className="flex-1 truncate">{item.label}</span>}
              {collapsed && (
                <div className="absolute left-full ml-2 px-2 py-1 bg-foreground text-primary-foreground text-xs rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-50 transition-opacity duration-150">
                  {item.label}
                </div>
              )}
            </Link>
          );
        })}
      </nav>

      {/* Bottom */}
      <div className="border-t border-border p-2 space-y-1">
        <div className={`flex items-center gap-2 px-2 py-2 rounded-lg ${collapsed ? 'justify-center' : ''}`}>
          <div className="w-7 h-7 rounded-full bg-primary flex items-center justify-center text-primary-foreground text-xs font-700 shrink-0">
            {userInitials || (portal === 'admin' ? 'A' : portal === 'customer' ? 'C' : 'T')}
          </div>
          {!collapsed && (
            <div className="flex flex-col leading-tight overflow-hidden">
              <span className="text-xs font-600 text-foreground truncate">
                {userName || (portal === 'admin' ? 'Admin' : portal === 'customer' ? 'Customer' : 'Technician')}
              </span>
              <span className="text-xs text-muted-foreground truncate">
                {userEmail || ''}
              </span>
            </div>
          )}
        </div>
        <button
          onClick={handleSignOut}
          className="w-full flex items-center gap-2 px-2 py-2 rounded-lg nav-item text-sm cursor-pointer"
          title={collapsed ? 'Sign Out' : undefined}
        >
          <LogOut size={16} className="shrink-0" />
          {!collapsed && <span>Sign Out</span>}
        </button>
      </div>

      {/* Toggle button */}
      <button
        onClick={onToggle}
        className="absolute -right-3 top-16 w-6 h-6 bg-card border border-border rounded-full flex items-center justify-center shadow-sm hover:bg-muted transition-colors z-40"
        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        {collapsed ? <ChevronRight size={12} /> : <ChevronLeft size={12} />}
      </button>
    </aside>
  );
}